import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const res = await API.post("/auth/register", { email, password, name });
        setUser(res.data);
      } else {
        const res = await API.post("/auth/login", { email, password });
        setUser(res.data);
      }
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1763890965393-1cea435581ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxzb290aGluZyUyMGxpYnJhcnklMjBkZXNrfGVufDB8fHx8MTc3NDcyNjg5N3ww&ixlib=rb-4.1.0&q=85"
          alt="Library"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#37352F]/30" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-4xl text-white font-medium leading-tight">
            Track your goals
            <br />
            like chapters in a book
          </h2>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FFFFFF]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-2 mb-8">
            <BookOpen className="w-7 h-7 text-[#2383E2]" strokeWidth={1.5} />
            <h1 className="text-3xl font-semibold text-[#37352F]">
              Codex
            </h1>
          </div>

          <h2 className="text-2xl text-[#37352F] mb-6">
            {isRegister ? "Create your account" : "Welcome back"}
          </h2>

          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full mb-6 border-[#E0E0DE] bg-white hover:bg-[#F7F6F3] text-[#37352F]"
            data-testid="google-login-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[#E0E0DE]" />
            <span className="text-xs text-[#787774] uppercase tracking-widest">
              or
            </span>
            <div className="flex-1 h-px bg-[#E0E0DE]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label className="text-xs tracking-wide uppercase text-[#787774]">
                  Name
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1 bg-transparent border-[#E0E0DE] focus:border-[#2383E2]"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs tracking-wide uppercase text-[#787774]">
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 bg-transparent border-[#E0E0DE] focus:border-[#2383E2]"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label className="text-xs tracking-wide uppercase text-[#787774]">
                Password
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 bg-transparent border-[#E0E0DE] focus:border-[#2383E2]"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2383E2] hover:bg-[#1B6EC2] text-white"
              data-testid="auth-submit-button"
            >
              {loading
                ? "Please wait..."
                : isRegister
                  ? "Create account"
                  : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-[#787774]">
            {isRegister
              ? "Already have an account?"
              : "Don't have an account?"}{" "}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#2383E2] hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? "Sign in" : "Create one"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
